import type { eIconType } from '@Primitive/SettingsListType';
import type { TreeItemsDict, IPivotConfig, IPivotField, TreeItem } from '@Primitive/TreeStruct';
// =================================================================================================
/**
 * Tree's items
 */
export interface TreeDataButton {
    /**
     * Internal key
     */
    key: string;

    /**
     * External Key
     */
    id: string;

    /**
     * Button's tooltip
     */
    tooltip: string;

    /**
     * Button's icon
     */
    icon: string;

    onClick?: (item: TreeItem<any>, foldersPivotKeys?: IPivotField[], pivotKey?: string) => void
}
// -------------------------------------------------------------------------------------------------
export interface TreeData {
    /**
     * Whether to display toolbar with actions or not.
     */
    showToolbar: boolean;

    /**
     * Override generic
     */
    overrideGlobalTheme: boolean;

    /**
     * Toolbar background color.
     */
    toolbarBgColor: string;

    /**
     * Toolbar icons color.
     */
    toolbarIconColor: string;

    /**
     * If toolbar is shown, whether to support search or not.
     */
    supportSearch: boolean;

    /**
     * Searchbar background color.
     */
    searchbarBgColor: string;

    /**
     * Searchbar text color.
     */
    searchbarTextColor: string;

    /**
     * If toolbar is shown, whether to draw buttons for expanding/collapsing tree.
     */
    supportExpandCollapse: boolean;

    /**
     * Custom buttons to drawn after expand/collapse and search buttons.
     */
    toolbarButtons: TreeDataButton[];

    /**
     * Custom buttons to drawn as dropdown at each item.
     */
    itemButtons: TreeDataButton[];

    /**
     * Whether to support pivot or not.
     */
    supportPivot: boolean;

    /**
     * Whether to start with expanded tree.
     */
    initiallyExpanded: boolean;

    /**
     * Make folders (from pivoting) selectable.
     */
    selectableFolders: boolean;

    /**
     * If pivot support is enabled, create a dropdown
     * list on toolbar to apply the configured ones.
     */
    pivotsConfig: IPivotConfig[];

    /**
     * Message to show when tree is empty
     */
    noItemsMessage: string;

    /**
     * Message to show when search has no match
     */
    noMatchedItemsMessage: string;

    /**
     * Item types. For each type the developer can select an icon
     */
    types: {
        /**
         * Internal key
         */
        key: string;

        /**
         * Path to field
         */
        name: string;

        /**
         * Custom icon for current item type
         */
        icon: string;

        /**
         * The icon's type (Font Awesome, URL or upload).
         */
        iconType: eIconType;
    }[];

    /**
     * It specifies that multiple tree items or/and nodes can be selected at once.
     */
    multipleSelection: boolean;

    /**
     * Tree background color
     */
    treeBackgroundColor: string;

    /**
     * Tree font color
     */
    treeFontColor: string;

    /**
     * Tree Select color
     */
    treeSelectColor: string;

    /**
     * Dropdown Button Text/Icon color
     */
    treeDropdownTextIconColor: string;
    /**
     * stops event propagation - the user can't select any item
     */
    readonly: boolean;

    /**
     * Show/Hide Tooltip
     */
    supportTooltip: boolean;

    /**
     * Json Property field for tooltip
     */
    tooltipField: string;

    /**
     * Define the order of toolbar items
     */
    toolbarIconOrdering: ToolbarOrderingItem[];

    /**
     * Define whether the right buttons will be show on non-leaf items
     */
    supportRightButtons?: boolean;
}
// -------------------------------------------------------------------------------------------------
export const enum ToolbarIconsIds {
    Pivot = "pivot",
    UserIcons = "user-icons",
    Search = "search",
    ExpandFolders = "expand-folders",
    CollapseFolders = "collapse-folders"
}
// -------------------------------------------------------------------------------------------------
export interface ToolbarOrderingItem {
    id: ToolbarIconsIds;
    icon: string;
    label: string;

    // TODO: support from the UI only the readonly
    visible: boolean;
}
// -------------------------------------------------------------------------------------------------
export interface TreeWidgetState {
    selectedItems: string[];
    showListSearch: boolean;
    items: TreeItemsDict;
    selectedPivot: string;
}
// -------------------------------------------------------------------------------------------------
export interface TreeWidgetStateSaved {
    selectedItems: string[];
    selectedPivot: string;
    FolderState: { [key: string]: boolean };
    TS: number;
}
// -------------------------------------------------------------------------------------------------
export interface DropdownButtonClickedEvent {
    isFolder: boolean;
    buttonKey: string;
    buttonLabel: string;
    data: any;
}
// -------------------------------------------------------------------------------------------------
export const enum PortKeys {
    dataPort = 'data',
    insertOrUpdate = 'insert_or_update',
    selectItem = 'select_item',
    selectedItem = 'selected_item',
    toolbarButtonClicked = 'toolbar_button_clicked',
    pivotDropdownButtonClicked = 'pivot_dropdown_button_clicked'
}
// ------------------------------------------------------------------------------------------------
export const enum LocalizationKeys {
    showSearch = 'show-search',
    hideSearch = 'hide-search',
    expandTree = 'expand-tree',
    collapseTree = 'collapse-tree',
    noItemsMessage = 'no-items',
    noMatchedItemsMessage = 'no-matched-items',
    pivotTree = 'pivot-tree'
}

export const defaultLocalizationValues = {
    [LocalizationKeys.showSearch]: 'Show search',
    [LocalizationKeys.hideSearch]: 'Hide search',
    [LocalizationKeys.expandTree]: 'Expand tree',
    [LocalizationKeys.collapseTree]: 'Collapse tree',
    [LocalizationKeys.pivotTree]: 'Pivot'
}
// -------------------------------------------------------------------------------------------------